import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppMessagingContext, AppMessagingContextType } from './app-messaging-context';
import { AppMessage, AppMessageMode } from './types';

interface AppMessagingProviderProps extends PropsWithChildren {
  mode: AppMessageMode;
  /**
   * Child mode: origins allowed to control this app (the shells that may embed
   * it). When configured, messages from any other origin are dropped. When not
   * configured, the source must still be the embedding parent window, but the
   * origin is not checked (and a config warning is logged once) — set this in
   * every deployed environment.
   */
  trustedOrigins?: string[];
}

type RegisteredChild = { iframe: HTMLIFrameElement; origin: string };

const originFromUrl = (url: string): string | null => {
  try {
    return new URL(url, window.location.href).origin;
  } catch {
    return null;
  }
};

/**
 * The embedding shell's origin, if it can be determined.
 * `ancestorOrigins` is authoritative and stable; `document.referrer` is only a
 * first-boot hint — after a child self-reload (OTA update, Vite optimizer) the
 * referrer becomes the child's OWN origin, and posting to it makes the browser
 * silently drop every child→parent message (the cause of the embedded-app
 * "stuck at login" failure). A referrer equal to our own origin is therefore
 * treated as unknown.
 */
const getParentOrigin = (): string | null => {
  const ancestors = window.location.ancestorOrigins;
  if (ancestors && ancestors.length > 0 && ancestors[0] && ancestors[0] !== 'null') {
    return ancestors[0];
  }

  const referrerOrigin = originFromUrl(document.referrer || '');
  if (referrerOrigin && referrerOrigin !== window.location.origin) {
    return referrerOrigin;
  }

  return null;
};

export const AppMessagingProvider = ({
  children,
  mode,
  trustedOrigins,
}: AppMessagingProviderProps) => {
  const messageListeners = useRef(new Set<(message: AppMessage) => void>());
  const registeredIframes = useRef(new Map<string, RegisteredChild>());
  const warnedUnconfiguredRef = useRef(false);
  const [isInitialised, setIsInitialised] = useState(false);

  const trustedOriginsRef = useRef(trustedOrigins);
  trustedOriginsRef.current = trustedOrigins;

  // Trust rules (specs/016-global-nav-venue-sync/contracts/shell-app-messaging.md):
  // accept only events from the registered counterpart; drop and log the rest.
  const isTrustedEvent = useCallback(
    (event: MessageEvent): boolean => {
      if (mode === 'parent') {
        for (const { iframe, origin } of registeredIframes.current.values()) {
          if (iframe.contentWindow === event.source && origin === event.origin) {
            return true;
          }
        }
        return false;
      }

      if (event.source !== window.parent) {
        return false;
      }

      const trusted = trustedOriginsRef.current;
      if (!trusted || trusted.length === 0) {
        if (!warnedUnconfiguredRef.current) {
          warnedUnconfiguredRef.current = true;
          console.warn(
            '[AppMessaging] trustedOrigins is not configured — accepting messages from any embedding parent. Configure it for this environment.',
          );
        }
        return true;
      }

      return trusted.includes(event.origin);
    },
    [mode],
  );

  // Handle incoming messages
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      const message = event.data as AppMessage | undefined;

      if (!message || typeof message !== 'object' || !message.type) {
        return;
      }

      if (!isTrustedEvent(event)) {
        console.warn(`[AppMessaging:${mode}] Dropped message from untrusted source`, {
          type: message.type,
          origin: event.origin,
        });
        return;
      }

      if (mode === 'child' && message.type === 'init') {
        setIsInitialised(true);
      }

      // Call all registered listeners
      messageListeners.current.forEach((callback) => callback(message));
    },
    [mode, isTrustedEvent],
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  const send = useCallback(
    (message: AppMessage) => {
      if (mode === 'child') {
        // Child sends to the embedding shell. Child→parent payloads carry no
        // credentials; the shell validates source+origin on receipt.
        const parentOrigin = getParentOrigin();
        const trusted = trustedOriginsRef.current;

        if (trusted && trusted.length > 0) {
          if (parentOrigin && trusted.includes(parentOrigin)) {
            window.parent.postMessage(message, parentOrigin);
          } else {
            // Parent origin unknown (e.g. after a self-reload in a browser
            // without ancestorOrigins): address every trusted shell — the
            // browser delivers only to the matching one and silently drops
            // the rest. Never '*' when a trusted list is configured.
            trusted.forEach((origin) => window.parent.postMessage(message, origin));
          }
        } else {
          window.parent.postMessage(message, parentOrigin ?? '*');
        }
      } else {
        // Parent mode has no self-messaging: nothing legitimate listens to a
        // shell's own window (see spec 016). Kept as a no-op-ish local post
        // for standalone apps that call send() unconditionally.
        window.postMessage(message, window.location.origin);
      }
    },
    [mode],
  );

  useEffect(() => {
    if (mode === 'parent') {
      return;
    }

    send({ type: 'app-ready' }); // send out the bat signal
  }, [mode, send]);

  const onMessage = useCallback((callback: (message: AppMessage) => void) => {
    messageListeners.current.add(callback);
    return () => messageListeners.current.delete(callback);
  }, []);

  /**
   * Register a child iframe (parent only). Must be called before sending
   * messages to the child. The origin defaults to the iframe src's origin;
   * pass it explicitly to pin the configured value.
   */
  const registerIframe = useCallback((id: string, iframe: HTMLIFrameElement, origin?: string) => {
    const resolvedOrigin = origin ?? originFromUrl(iframe.src);
    if (!resolvedOrigin) {
      throw new Error(`Cannot register child iframe "${id}" without a resolvable origin.`);
    }
    registeredIframes.current.set(id, { iframe, origin: resolvedOrigin });
  }, []);

  const unregisterIframe = useCallback((id: string) => {
    registeredIframes.current.delete(id);
  }, []);

  /**
   * Send message to a specific registered child iframe, addressed to its
   * registered origin (never '*').
   */
  const sendToChild = useCallback((childId: string, message: AppMessage) => {
    const child = registeredIframes.current.get(childId);
    if (!child?.iframe.contentWindow) {
      throw new Error(`Child iframe "${childId}" is not registered. Call registerIframe() first.`);
    }
    child.iframe.contentWindow.postMessage(message, child.origin);
  }, []);

  /**
   * Broadcast message to all registered child iframes (origin-addressed).
   */
  const broadcastToChildren = useCallback((message: AppMessage) => {
    registeredIframes.current.forEach(({ iframe, origin }) => {
      if (iframe.contentWindow) {
        iframe.contentWindow.postMessage(message, origin);
      }
    });
  }, []);

  const value: AppMessagingContextType = useMemo(
    () => ({
      mode,
      isInitialised,
      send,
      onMessage,
      registerIframe,
      unregisterIframe,
      sendToChild,
      broadcastToChildren,
    }),
    [
      mode,
      isInitialised,
      send,
      onMessage,
      registerIframe,
      unregisterIframe,
      sendToChild,
      broadcastToChildren,
    ],
  );

  return <AppMessagingContext.Provider value={value}>{children}</AppMessagingContext.Provider>;
};
